// handling selected global vars to be shared between pages via localstorage

import storage from "./localstorage";
import { copyProps, getAnyProp, getProp, isEmptyObject, isObject, jsonParse } from "./util";
import { getPropertyStateKeys } from "./globals";
import { getGlobal, setGlobal, setGlobalValue, getGlobalValue } from "./global-util";
import getLogger from "./debug-logger";
import { filterRequestedProducts } from "./products";

const log = getLogger("stored-data", 2);

//------------------------------------------------------
// store/retrieve last property address for reset purpose
//------------------------------------------------------
const initAddressKey = "init_address";

export const storeInitAddress = (addrLine) => {
  storage.set(initAddressKey, addrLine);
};

export const popInitAddress = () => storage.pop(initAddressKey);
//------------------------------------------------------

//--------------- CUSTOM STORAGE UNIT -------------------

const dataTTL = 1000 * 60 * 60 * 3;

const myStorageKey = "avm_property_data";

//------------------------------------------------------
// Read/validate custom storage object
//------------------------------------------------------
const readStoredData = () => {
  const obj = storage.get(myStorageKey);
  if (isObject(obj)) {
    const expTime = Number(getProp(obj, "DATA_EXPIRES"));
    if (!!expTime && expTime > Date.now()) {
      return { ...obj };
    }
  }
  return null;
};

//------------------------------------------------------
// check if last property data in localstorage exists
//------------------------------------------------------
export const storedPropertyDataExists = () => !!getStoredPropertyData();

//------------------------------------------------------
// write property data into localstorage
//------------------------------------------------------
const setStoredData = (obj) => {
  if (!isObject(obj)) {
    log(`object expected. Actual value:`, obj); //---------------- log
    return;
  }
  obj.DATA_EXPIRES = Date.now() + dataTTL;
  storage.set(myStorageKey, obj);
};

//------------------------------------------------------
// delete custom storage unit from localstorage
//------------------------------------------------------
export const removeStoredData = () => {
  log(`removing property info from locastorage`); //--------- log
  storage.remove(myStorageKey);
};

//------------------------------------------------------
// read property data from localstorage, validating its integrity and expiration
//------------------------------------------------------
export const getStoredPropertyData = () => {
  const obj = readStoredData() || {};
  if (!getProp(obj, "FEATURES_CURRENT") || (!getProp(obj, "ADDRESS_LINE") && !getProp(obj, "ADDRESS_LEGAL_LINE"))) {
    return null;
  }
  return obj;
};

//------------------------------------------------------
// collect current property data and put it in localstopage
//------------------------------------------------------
export const storePropertyData = () => {
  const obj = getGlobal();
  // keys of all property-related globals
  const keys = [...getPropertyStateKeys()].filter((k) => getProp(obj, k, null) !== null);
  const propertyInfo = copyProps(keys, obj);
  setStoredData(propertyInfo);
};

//------------------------------------------------------
// update stored property data and save it in localstopage
//------------------------------------------------------
export const updateStoredData = (obj) => {
  if (!isObject(obj) || isEmptyObject(obj)) {
    log(`non-empty data object expected, received `, obj); //-------------- log
    return;
  }
  log(`preparing updates`, obj); //------------------------------------- log
  const info = readStoredData() || {};
  const updated = { ...info, ...obj };
  setStoredData(updated);
};

//------------------------------------------------------
// update current data snapshot by single data point
//------------------------------------------------------
export const setStoredValue = (key, value) => {
  if (!key) {
    log(`data key is required`); //-------------------------------------- log
    return;
  }
  const stored = readStoredData() || {};
  if (getProp(stored, key) === value) {
    return;
  }
  log(`updating stored data for key: ${key} with value:`, value); //---------------- log
  stored[key] = value;
  setStoredData(stored);
};

//------------------------------------------------------
// update stored data in sync with globals
//------------------------------------------------------
export const setAndStoreGlobals = (obj) => {
  if (isObject(obj)) {
    updateStoredData(obj);
    setGlobal(obj);
  } else {
    log("setAndStoreGlobals: object expected. Found:", obj); //------------------ log
  }
};

//------------------------------------------------------
// update current data snapshot and global object by single data point
//------------------------------------------------------
export const setAndStoreGlobalValue = (key, val) => {
  setStoredValue(key, val);
  setGlobalValue(key, val);
};

//------------------------------------------------------
// keep track on requested products in localstorage
//------------------------------------------------------
export const storeRequestedProducts = (prodList) => {
  const newProducts = Array.isArray(prodList) ? prodList : [];
  const globalProducts = getGlobalValue("REQUESTED_PRODUCTS") || [];
  const storedProducts = getProp(readStoredData(), "REQUESTED_PRODUCTS", []);
  let reqProducts = Array.from(new Set([...globalProducts, ...storedProducts, ...newProducts]));
  // remove untracked products from the list
  reqProducts = filterRequestedProducts(reqProducts);
  log(`storing requested products:`, reqProducts); //------------------ log
  setAndStoreGlobalValue("REQUESTED_PRODUCTS", reqProducts);
};

//------------------------------------------------------
// restore last saved property data and update global object
//------------------------------------------------------
export const restorePropertyData = () => {
  const storedData = getStoredPropertyData();
  if (!storedData) {
    log(`no stored property data found`); //------------------ log
    return false;
  }
  log(`property info from localstorage`, storedData); //------------------ log

  const updates = { ...storedData, ...jsonParse(storedData.FEATURES_CURRENT) };

  //log(`restoring property info from localstorage`, updates); //--------- log
  updates.PROPERTY_SOURCE = "cache";
  setGlobal(updates);

  return true;
};

//------------------------------------------------------
// check if valid property data exists in localstorage
//------------------------------------------------------
export const storedDataExists = () => !!getStoredPropertyData();

//------------------------------------------------------
// invalidate PDF report and Property Snapshot PDF URLs - global and stored
//------------------------------------------------------
export const dropReport = () => {
  const upd = { REPORT_URL: null, SNAPSHOT_URL: null };
  setGlobal(upd);
  updateStoredData(upd);
};

//--------------------------------------------------------------
// committing changes in property stats
//--------------------------------------------------------------
export const saveUpdatedStats = () => {
  const glob = getGlobal();
  const statsJson = getProp(glob, "STATS_CURRENT");
  const prevStatsJson = getAnyProp(glob, "STATS_INITIAL", "STATS_UPDATED");
  if (statsJson && statsJson !== prevStatsJson) {
    log(`updating property stats`, jsonParse(statsJson)); //----------------------- log
    setAndStoreGlobalValue("STATS_UPDATED", statsJson);
  }
};
